import React from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { Button } from 'react-bootstrap';
import classNames from 'classnames';
import { ReactComponent as CloseIcon } from '../../../../../../../shared/icons/close-icon.svg';
import RequestReviewViaEmailForm from './RequestReviewViaEmailForm';
import { TitleFormattedMessage } from '../../../../../../titleFormattedMessage';
import styles from './ReviewViaEmailSlideout.module.scss';

interface ReviewViaEmailSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  dealId: string;
  enableAttachments: boolean;
  disableWordExport?: boolean;
  extraAttachments?: any[];
  documentSource?: string;
}

const ReviewViaEmailSlideout = ({
  isOpen,
  onClose,
  teamId,
  dealId,
  enableAttachments,
  disableWordExport = false,
  extraAttachments = [],
  documentSource,
}: ReviewViaEmailSlideoutProps) => {
  // Don't render anything if not showing
  if (!isOpen) {
    return null;
  }

  // Memoized container components for RequestReviewViaEmailForm
  const BodyContainer = React.memo(({ children }: { children?: React.ReactNode }) => (
    <div className={styles.slideout__formSection}>{children}</div>
  ));

  const ActionsContainer = React.memo(({ children }: { children?: React.ReactNode }) => (
    <div className={styles.slideout__footer}>{children}</div>
  ));

  // Use React Portal to render slideout outside current DOM tree
  return ReactDOM.createPortal(
    <>
      {/* Dark overlay backdrop */}
      {isOpen && <div className={styles.slideoutBackdrop} />}

      <div
        className={classNames(
          styles.slideoutWrapper,
          isOpen ? styles.slideoutWrapper__show : styles.slideoutWrapper__collapsed
        )}
      >
        <div className={styles.slideout}>
          {/* Sticky Header */}
          <div className={styles.slideout__header}>
            <div className={styles.slideout__title}>
              <FormattedMessage id="title.add-team-member-to-deal" defaultMessage="Email Document(s) for review" />
            </div>
            <div className={styles.slideout__close} onClick={onClose}>
              <CloseIcon />
            </div>
          </div>

          {/* Scrollable Content */}
          <div className={styles.slideout__content}>
            <RequestReviewViaEmailForm
              bodyContainer={BodyContainer}
              actionsContainer={ActionsContainer}
              renderSecondaryAction={() => (
                <Button variant="link" className="text-secondary" onClick={onClose}>
                  <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
                </Button>
              )}
              onSuccess={onClose}
              teamId={teamId}
              dealId={dealId}
              enableAttachments={enableAttachments}
              disableWordExport={disableWordExport}
              extraAttachments={extraAttachments}
              documentSource={documentSource}
            />
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ReviewViaEmailSlideout;
